import type { NoteName, Scale } from '@tunasong/schemas'
import type { ChordType, ChordVariant } from '../chord/chord-types.js'
import ScaleLib from '../scale/scale.js'

/**
 * Function Quiz v1 (Phase 2 Tier 1) — pure-function core.
 *
 * Closes the originally-planned Tier 1 toolset (Melody Writer + Cadence Lab +
 * Function Quiz). v1 ships major + minor keys only, with three answer
 * categories (Tonic / Predominant / Dominant) and a 10-question session.
 *
 * Spec: docs/superpowers/specs/2026-05-18-function-quiz-design.md
 * Bowie scope memo: .claude/agent-memory/bowie/project_function_quiz_v1_scope.md
 */

export type FunctionCategory = 'tonic' | 'predominant' | 'dominant'

type ChordQuality = 'major' | 'minor' | 'diminished'

const QUALITY_VARIANT: Record<ChordQuality, ChordType> = {
  major: '',
  minor: 'm',
  diminished: 'dim',
}

/**
 * Quiz-pool entry: a Roman label + its function category answer(s),
 * keyed off `keyType` so the question generator picks from the right
 * sub-pool. Internal `degreeIndex` / `quality` drive the concrete-chord
 * builder; `lexiconId` references the existing Phase-1.5 function-* entry.
 */
export interface QuizChordEntry {
  /** Analyzer-emitted Roman label — drives feedback wording. */
  roman: string
  /** Lexicon id for chord-specific feedback copy. */
  lexiconId: string
  primary: FunctionCategory
  /** Set ONLY for the three multi-valued chords (Bowie §3.4). */
  secondary?: FunctionCategory
  keyType: 'major' | 'minor'
  /** 0-indexed position into `ScaleLib.getNotes(scale)` for the chord root. */
  degreeIndex: number
  /** Explicit chord quality (NOT derived from the diatonic table — see spec §10.1). */
  quality: ChordQuality
}

/**
 * Bowie-locked quiz pool (§3.3 + §3.4). 13 entries — 7 major + 6 minor.
 * Minor's ♭VII is deliberately excluded (pedagogically ambiguous).
 */
export const QUIZ_POOL: readonly QuizChordEntry[] = [
  // ───────── Major-key (7) ─────────
  { roman: 'I', lexiconId: 'function-I', primary: 'tonic', keyType: 'major', degreeIndex: 0, quality: 'major' },
  { roman: 'ii', lexiconId: 'function-ii', primary: 'predominant', keyType: 'major', degreeIndex: 1, quality: 'minor' },
  {
    roman: 'iii',
    lexiconId: 'function-iii',
    primary: 'tonic',
    secondary: 'predominant',
    keyType: 'major',
    degreeIndex: 2,
    quality: 'minor',
  },
  { roman: 'IV', lexiconId: 'function-IV', primary: 'predominant', keyType: 'major', degreeIndex: 3, quality: 'major' },
  { roman: 'V', lexiconId: 'function-V', primary: 'dominant', keyType: 'major', degreeIndex: 4, quality: 'major' },
  {
    roman: 'vi',
    lexiconId: 'function-vi',
    primary: 'tonic',
    secondary: 'predominant',
    keyType: 'major',
    degreeIndex: 5,
    quality: 'minor',
  },
  {
    roman: 'vii°',
    lexiconId: 'function-vii-dim',
    primary: 'dominant',
    keyType: 'major',
    degreeIndex: 6,
    quality: 'diminished',
  },
  // ───────── Minor-key (6 — ♭VII excluded) ─────────
  { roman: 'i', lexiconId: 'function-i', primary: 'tonic', keyType: 'minor', degreeIndex: 0, quality: 'minor' },
  {
    roman: 'ii°',
    lexiconId: 'function-ii-dim',
    primary: 'predominant',
    keyType: 'minor',
    degreeIndex: 1,
    quality: 'diminished',
  },
  {
    roman: '♭III',
    lexiconId: 'function-flat-III',
    primary: 'tonic',
    keyType: 'minor',
    degreeIndex: 2,
    quality: 'major',
  },
  { roman: 'iv', lexiconId: 'function-iv', primary: 'predominant', keyType: 'minor', degreeIndex: 3, quality: 'minor' },
  // V in minor: Tunasong harmonic-minor convention — major V (PR #1343). Reuses
  // major-key `function-V` copy; per spec §8.6 the natural-minor `function-v`
  // entry isn't the right fit because the audition's V is the major triad.
  { roman: 'V', lexiconId: 'function-V', primary: 'dominant', keyType: 'minor', degreeIndex: 4, quality: 'major' },
  {
    roman: '♭VI',
    lexiconId: 'function-flat-VI',
    primary: 'tonic',
    secondary: 'predominant',
    keyType: 'minor',
    degreeIndex: 5,
    quality: 'major',
  },
]

/**
 * Circle-of-fifths-preferred tonic spellings per key type (Bowie §8.3 verdict).
 * Major and minor share most tonics; the two divergent slots are:
 *  - Major prefers `Db`/`Ab` (the canonical flat-major tonics)
 *  - Minor prefers `C#`/`G#` (the canonical sharp-minor tonics)
 * All 24 quiz keys keep ≤6 accidentals; no double-sharps or double-flats.
 *
 * Locked tables — do not use `getStandardNoteNames()` for the quiz pool.
 */
export const MAJOR_TONICS: readonly NoteName[] = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B']
export const MINOR_TONICS: readonly NoteName[] = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'G#', 'A', 'Bb', 'B']

/** A generated quiz question — drives the question panel render. */
export interface QuizQuestion {
  scale: Scale
  chord: ChordVariant
  tonicChord: ChordVariant
  entry: QuizChordEntry
}

const MAJOR_POOL: readonly QuizChordEntry[] = QUIZ_POOL.filter(e => e.keyType === 'major')
const MINOR_POOL: readonly QuizChordEntry[] = QUIZ_POOL.filter(e => e.keyType === 'minor')

/**
 * Generate a fresh random quiz question.
 *
 *  1. RNG[0] → key-type ('major' if < 0.5 else 'minor').
 *  2. RNG[1] → tonic from the key-type's locked table (Bowie §8.3).
 *  3. RNG[2] → quiz-pool entry within the key-type's sub-pool.
 *  4. Compute the concrete chord (root from ScaleLib.getNotes, quality from
 *     the entry's explicit `quality` — NOT from the diatonic table).
 *  5. Compute the tonic chord (I for major, i for minor).
 *
 * RNG is injectable for deterministic tests; defaults to `Math.random`.
 */
export const generateQuizQuestion = (rng: () => number = Math.random): QuizQuestion => {
  const keyType: 'major' | 'minor' = rng() < 0.5 ? 'major' : 'minor'
  const tonics = keyType === 'major' ? MAJOR_TONICS : MINOR_TONICS
  const tonic = tonics[Math.floor(rng() * tonics.length)]
  const pool = keyType === 'major' ? MAJOR_POOL : MINOR_POOL
  const entry = pool[Math.floor(rng() * pool.length)]
  const scale: Scale = { root: tonic, type: keyType }
  const notes = ScaleLib.getNotes(scale)
  const chord: ChordVariant = {
    root: notes[entry.degreeIndex],
    variant: QUALITY_VARIANT[entry.quality],
  }
  const tonicChord: ChordVariant = {
    root: tonic,
    variant: keyType === 'major' ? '' : 'm',
  }
  return { scale, chord, tonicChord, entry }
}

/** Validation verdict — drives feedback panel rendering. */
export interface AnswerVerdict {
  /** True when picked is the primary or (when set) secondary. */
  correct: boolean
  /** Which slot the user matched, if any. Drives feedback wording. */
  matchedAs: 'primary' | 'secondary' | null
  /** The question's quiz-pool entry — caller renders its lexicon copy. */
  entry: QuizChordEntry
}

export const validateAnswer = (question: QuizQuestion, picked: FunctionCategory): AnswerVerdict => {
  const { entry } = question
  if (picked === entry.primary) {
    return { correct: true, matchedAs: 'primary', entry }
  }
  if (entry.secondary && picked === entry.secondary) {
    return { correct: true, matchedAs: 'secondary', entry }
  }
  return { correct: false, matchedAs: null, entry }
}

/**
 * Multi-valued-chord feedback suffixes (Bowie §8.1 verdict — feedback-site
 * augmentation, NOT lexicon extension). Each suffix names BOTH roles and
 * explains when each applies. Three entries: major iii, major vi (Bowie-
 * revised wording — "substituting for IV", the unambiguous framing), and
 * minor ♭VI. Returns null for single-valued chords.
 */
const MULTI_VALUED_SUFFIX: Record<string, string> = {
  // major iii
  'function-iii':
    "iii's primary role is Tonic-substitute, but it also functions as Predominant in progressions like iii → vi → ii → V.",
  // major vi — Bowie-revised wording (substituting for IV, not vi→ii→V→I)
  'function-vi':
    'vi is most often a Tonic (relative-minor substitute), but also functions as Predominant — substituting for IV (the canonical predominant) when followed by V.',
  // minor ♭VI
  'function-flat-VI':
    '♭VI is most often a Tonic-substitute in minor (the deceptive-cadence target), but it can also serve as Predominant leading into V.',
}

export const getMultiValuedSuffix = (entry: QuizChordEntry): string | null => {
  if (!entry.secondary) {
    return null
  }
  return MULTI_VALUED_SUFFIX[entry.lexiconId] ?? null
}

/** Session-summary helpers. */
export interface SessionStats {
  total: number
  correct: number
  /** Per-category correct/total — keyed by the question's PRIMARY category. */
  byCategory: Record<FunctionCategory, { correct: number; total: number }>
}

export interface SessionResult {
  question: QuizQuestion
  verdict: AnswerVerdict
}

const zeroBuckets = (): SessionStats['byCategory'] => ({
  tonic: { correct: 0, total: 0 },
  predominant: { correct: 0, total: 0 },
  dominant: { correct: 0, total: 0 },
})

/**
 * Fold a list of question/verdict pairs into session-summary stats.
 * Each result is counted under the question's PRIMARY category — the
 * editorial framing (Bowie §3.4) is that primary is the "main" answer,
 * and the summary should reflect how the user did on each conceptual
 * category, not on each chord's secondary alternative.
 */
export const computeSessionStats = (results: SessionResult[]): SessionStats => {
  const byCategory = zeroBuckets()
  let correct = 0
  for (const { question, verdict } of results) {
    const cat = question.entry.primary
    byCategory[cat].total += 1
    if (verdict.correct) {
      byCategory[cat].correct += 1
      correct += 1
    }
  }
  return { total: results.length, correct, byCategory }
}
